import type { TaskRecord } from "../../backend/api/generated/openapi";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useState } from "react";
import { useShiftTaskOptions } from "../../backend/queries/tasks";
import { Checkbox } from "../../components/ui/checkbox";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  EmptyState,
  ErrorState,
  LoadingState,
} from "../../components/patterns";

export function ShiftTaskPicker({
  selected,
  onChange,
  enabled,
  agentId,
}: {
  selected: string[];
  onChange: (ids: string[]) => void;
  enabled: boolean;
  agentId: string;
}) {
  const [search, setSearch] = useState("");
  const query = useDebouncedValue(search);
  const tasks = useShiftTaskOptions(enabled, query, agentId);
  const options: TaskRecord[] = tasks.data || [];
  if (!agentId)
    return (
      <EmptyState
        title="Choose an agent first"
        description="Then select tasks from their assigned projects."
      />
    );
  return (
    <fieldset className="grid gap-2">
      <legend className="mb-2 text-sm font-medium">
        Assigned project tasks ({selected.length}/50 selected)
      </legend>
      <Label htmlFor="shift-task-search" className="sr-only">
        Search tasks
      </Label>
      <Input
        id="shift-task-search"
        placeholder="Search task titles or project number"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />
      {tasks.isPending ? (
        <LoadingState label="Loading tasks" />
      ) : tasks.error ? (
        <ErrorState onRetry={() => tasks.refetch()} />
      ) : !options.length ? (
        <EmptyState
          title="No matching tasks"
          description="Assign this agent to a project as a lead or supporting agent, then add tasks to that project. Clear the search to see all matching tasks."
        />
      ) : (
        <div className="max-h-48 overflow-y-auto rounded-md border divide-y">
          {Array.from(new Set<number>(options.map((task) => task.project_id))).map(
            (projectId) => (
              <details key={projectId} open>
                <summary className="cursor-pointer bg-muted px-3 py-2 text-sm font-semibold">
                  Project #{projectId}
                </summary>
                {options
                  .filter((task) => task.project_id === projectId)
                  .map((task) => (
                    <label
                      key={task.id}
                      className="flex items-center gap-3 p-3 text-sm hover:bg-muted"
                    >
                      <Checkbox
                        checked={selected.includes(task.id)}
                        disabled={
                          !selected.includes(task.id) && selected.length >= 50
                        }
                        onCheckedChange={(checked) =>
                          onChange(
                            checked
                              ? [...selected, task.id]
                              : selected.filter((id) => id !== task.id),
                          )
                        }
                      />
                      <span>
                        {task.title || "Untitled task"}
                        <span className="block text-xs text-muted-foreground">
                          Project #{task.project_id}
                          {task.is_complete ? " · Completed" : ""}
                        </span>
                      </span>
                    </label>
                  ))}
              </details>
            ),
          )}
        </div>
      )}
      {options.length === 100 && (
        <p className="text-xs text-muted-foreground">
          Showing 100 matches. Refine your search to find other tasks.
        </p>
      )}
    </fieldset>
  );
}
